import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { AlertTriangle, MapPin, Clock, User, Camera, X, Phone, Check, Send, Loader2, Upload, Image as ImageIcon, HandMetal } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage } from '../utils/imageCompression';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SegnalazioniSection = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [description, setDescription] = useState('');
  const [phone, setPhone] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [compressing, setCompressing] = useState(false);
  
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const fetchReports = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/waste-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReports();
    
    // Get GPS
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => console.error('GPS error:', error)
      );
    }
  }, [fetchReports]);

  const handlePhotoSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCompressing(true);
    try {
      const compressed = await compressImage(file, 800, 0.7);
      setPhoto(compressed.base64);
      setPhotoPreview(compressed.base64);
    } catch (error) {
      toast.error('Errore compressione foto');
    } finally {
      setCompressing(false);
    }
  }, []);

  const removePhoto = useCallback(() => {
    setPhoto(null);
    setPhotoPreview(null);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  }, []);

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error('Descrivi il rifiuto da segnalare');
      return;
    }
    
    if (!gpsLocation) {
      toast.error('Posizione GPS non disponibile');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/waste-reports`,
        {
          location: gpsLocation,
          description: description.trim(),
          photo_url: photo,
          phone: phone.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Segnalazione inviata alla community!');
      setShowForm(false);
      setDescription('');
      setPhone('');
      setPhoto(null);
      setPhotoPreview(null);
      fetchReports();
    } catch (error) {
      console.error('Errore segnalazione:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Errore invio segnalazione');
    } finally {
      setSubmitting(false);
    }
  };

  const takeReport = async (reportId) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/waste-reports/${reportId}/take`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Hai preso in carico la segnalazione!');
      
      if (response.data.reporter_phone) {
        toast.info(`Contatto segnalatore: ${response.data.reporter_phone}`);
      }
      
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const resolveReport = async (reportId) => {
    if (!window.confirm('Confermi di aver rimosso il rifiuto?')) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/waste-reports/${reportId}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Grazie! Segnalazione risolta!');
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const cancelTake = async (reportId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/waste-reports/${reportId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.info('Presa in carico annullata');
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 pb-32">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-3">
          <AlertTriangle className="w-8 h-8 text-yellow-400" />
        </div>
        <h2 className="text-3xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
          <TranslatedText text="SEGNALAZIONI RIFIUTI" />
        </h2>
        <p className="text-slate-400 text-sm mt-1"><TranslatedText text="Segnala rifiuti da rimuovere alla community" /></p>
      </div>

      {/* Pulsante Nuova Segnalazione */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full mb-6 py-4 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg"
        >
          <AlertTriangle className="w-5 h-5" />
          <TranslatedText text="NUOVA SEGNALAZIONE" />
        </button>
      )}

      {/* Form Nuova Segnalazione */}
      {showForm && (
        <div className="bg-slate-900/80 border border-yellow-500/50 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-yellow-400"><TranslatedText text="Nuova Segnalazione" /></h3>
            <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* GPS Status */}
          <div className={`flex items-center gap-2 mb-4 p-2 rounded-lg ${gpsLocation ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
            <MapPin className="w-4 h-4" />
            <span className="text-sm">{gpsLocation ? 'Posizione GPS acquisita' : 'GPS non disponibile'}</span>
          </div>

          {/* Descrizione */}
          <div className="mb-4">
            <label className="text-xs text-slate-400 uppercase mb-1 block">Descrizione rifiuto *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Es. Pneumatico vicino agli scogli, frigorifero abbandonato..."
              className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-yellow-500 focus:outline-none resize-none"
              rows={3}
            />
          </div>

          {/* Foto */}
          <div className="mb-4">
            <label className="text-xs text-slate-400 uppercase mb-1 block">Foto (consigliata)</label>
            
            {photoPreview ? (
              <div className="relative">
                <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                <button
                  onClick={removePhoto}
                  className="absolute top-2 right-2 p-2 bg-red-500 rounded-full text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={compressing}
                  className="flex-1 py-3 border-2 border-dashed border-slate-700 rounded-lg text-slate-400 hover:border-yellow-500 flex items-center justify-center gap-2"
                >
                  <Camera className="w-5 h-5" />
                  <span>Scatta</span>
                </button>
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={compressing}
                  className="flex-1 py-3 border-2 border-dashed border-slate-700 rounded-lg text-slate-400 hover:border-yellow-500 flex items-center justify-center gap-2"
                >
                  <ImageIcon className="w-5 h-5" />
                  <span>Galleria</span>
                </button>
              </div>
            )}
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoSelect} className="hidden" />
            <input ref={galleryInputRef} type="file" accept="image/*" onChange={handlePhotoSelect} className="hidden" />
          </div>

          {/* Telefono (opzionale) */}
          <div className="mb-4">
            <label className="text-xs text-slate-400 uppercase mb-1 block">
              Tuo telefono <span className="text-slate-500">(opzionale, per essere contattato)</span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Es. 333 1234567"
              className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
            />
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting || !gpsLocation}
            className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-slate-700 text-black font-bold rounded-lg flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Invio...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Invia Segnalazione
              </>
            )}
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-slate-900/60 border border-yellow-500/30 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-400">{reports.filter(r => r.status === 'active').length}</p>
          <p className="text-xs text-slate-400">Da rimuovere</p>
        </div>
        <div className="bg-slate-900/60 border border-orange-500/30 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-orange-400">{reports.filter(r => r.status === 'taken').length}</p>
          <p className="text-xs text-slate-400">In carico</p>
        </div>
      </div>

      {/* Lista Segnalazioni */}
      <h3 className="text-lg font-semibold text-white mb-3">Segnalazioni Attive</h3>
      
      {reports.length === 0 ? (
        <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Nessuna segnalazione attiva</p>
          <p className="text-slate-500 text-sm mt-1">Sii il primo a segnalare rifiuti da rimuovere!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <div 
              key={report.id} 
              className={`bg-slate-900/60 border rounded-xl overflow-hidden ${
                report.status === 'active' ? 'border-yellow-500/50' : 'border-orange-500/50'
              }`}
            >
              {/* Header */}
              <div className="p-4">
                <div className="flex items-start gap-3">
                  {/* Icona stato */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    report.status === 'active' ? 'bg-yellow-500/20' : 'bg-orange-500/20'
                  }`}>
                    <AlertTriangle className={`w-5 h-5 ${
                      report.status === 'active' ? 'text-yellow-400' : 'text-orange-400'
                    }`} />
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium">{report.description}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {report.reporter_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(report.created_at)}
                      </span>
                    </div>
                    
                    {/* Stato "In carico" */}
                    {report.status === 'taken' && (
                      <div className="mt-2 px-2 py-1 bg-orange-500/20 rounded inline-flex items-center gap-1 text-xs text-orange-400">
                        <User className="w-3 h-3" />
                        In carico a: {report.taken_by_name}
                      </div>
                    )}
                  </div>
                </div>

                {/* Foto */}
                {report.photo_url && (
                  <img 
                    src={report.photo_url} 
                    alt="Rifiuto segnalato" 
                    className="w-full h-40 object-cover rounded-lg mt-3"
                  />
                )}
              </div>

              {/* Azioni */}
              <div className="px-4 pb-4 flex gap-2">
                {/* Pulsante "Prendi in carico" - per qualsiasi segnalazione attiva */}
                {report.can_take && (
                  <button
                    onClick={() => takeReport(report.id)}
                    className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg flex items-center justify-center gap-2"
                  >
                    <HandMetal className="w-4 h-4" />
                    Prendi in carico
                  </button>
                )}
                
                {/* Pulsante "Pulizia fatta" - solo se l'ho presa io */}
                {report.can_resolve && (
                  <>
                    <button
                      onClick={() => resolveReport(report.id)}
                      className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg flex items-center justify-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Pulizia fatta
                    </button>
                    <button
                      onClick={() => cancelTake(report.id)}
                      className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
                    >
                      Annulla
                    </button>
                  </>
                )}
                
                {/* Contatto segnalatore - se presa in carico da me e c'è telefono */}
                {report.can_resolve && report.reporter_phone && (
                  <a
                    href={`tel:${report.reporter_phone}`}
                    className="py-2 px-4 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg flex items-center gap-1"
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                )}
                
                {/* Info se presa da altri (non da me) */}
                {report.status === 'taken' && !report.can_resolve && (
                  <span className="flex-1 py-2 bg-orange-500/20 text-orange-400 text-sm rounded-lg flex items-center justify-center">
                    In carico a {report.taken_by_name}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SegnalazioniSection;
